import Loading from '@common/components/Loading';
import StatusBar from '@common/components/StatusBar';
import { appInfoSelector } from '@common/redux/selectors/app';
import { dimensionSelector } from '@common/redux/selectors/page';
import {
  setLoadingPage,
  setValueBindingScreen,
  removeValueBindingScreen,
} from '@common/redux/slice/page';
import { useLocation } from '@common/routes/hooks';
import { getBindingScreen } from '@common/services';
import { PropRender } from '@common/types/';
import {
  getAttributesFixed,
  getAttributesNormal,
  getPaddingBottomScreen,
} from '@common/utils';
import { actionPromise } from '@common/utils/handleActions/excuteAction';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { debounce, get, isEmpty, isNil } from 'lodash';
import {
  default as qs,
  default as queryString,
  ParsedQuery,
} from 'query-string';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';
import {
  EmitterSubscription,
  KeyboardAvoidingView,
  Linking,
  Platform,
  SafeAreaView,
  ScrollView,
  StatusBar as StatusBarRN,
  View,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { loadingPageSelector } from '../../redux/selectors/page';
import BackgroundImage from './BackgroundImage';
import RenderConnector from './Connector/RenderConnector';
import ObjectRender from './ObjectRender';
let eventDeepLinkUrl: EmitterSubscription;

function Render({ screen, dependencies }: PropRender) {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const appInfo = useSelector(appInfoSelector);
  const dimension = useSelector(dimensionSelector);
  const loadingPage = useSelector(loadingPageSelector);

  const { search } = useLocation();
  const { hiddenStatusBar, target, targetModal }: ParsedQuery<any> = useMemo(
    () => queryString.parse(search),
    [search]
  );

  const pathname = qs.parse(search);
  const ref: any = useRef();

  const fetBindingPage = useCallback(
    debounce(async (controller, screen, dependencies, appInfo) => {
      try {
        if (appInfo) {
          const data = await getBindingScreen({
            appId: appInfo?.id,
            dependencies,
            screenUuid: screen.screenUuid,
            controller,
          });
          !isEmpty(data) && dispatch(setValueBindingScreen(data));
        }
      } catch (error) {
        console.log('error', error);
      }
    }, 300),
    []
  );

  useEffect(() => {
    const controller = new AbortController();
    fetBindingPage(controller, screen, dependencies, appInfo);

    return () => {
      dispatch(removeValueBindingScreen({}));
      controller.abort();
    };
  }, [screen, dependencies, appInfo]);

  useLayoutEffect(() => {
    const { targetModal } = pathname;
    if (isNil(targetModal) || isEmpty(targetModal)) {
      ref?.current?.scrollTo({ y: 0, x: 0 });
    }
  }, [screen, ref]);

  const getCode = (url: any) => {
    if (Platform.OS === 'web') {
      const search = window.parent.location.search || window.location.search;
      const params = qs.parse(search);

      return params?.code;
    } else {
      if (url?.url) {
        const code = url?.url?.split('code=')[1] || null;

        return code;
      }
    }
  };

  const handleUrlDeepLink = async (url: any) => {
    const code = getCode(url);
    const pendingActions = await AsyncStorage.getItem('penddingActions');
    if (!pendingActions || !code) return;
    const actions = JSON.parse(pendingActions);
    dispatch(setLoadingPage(true));
    await actionPromise({
      objectId: actions?.objectId,
      actionId: actions?.actionId,
      clientValues: dependencies,
      screenUuid: actions?.screenUuid,
      screenAction: false,
      messages,
      arrayAction: actions?.arrayAction,
      data: { code: code },
    });
    await AsyncStorage.removeItem('penddingActions');
    dispatch(setLoadingPage(false));
  };

  useEffect(() => {
    Linking.getInitialURL().then((url) => {
      return handleUrlDeepLink({
        url,
      });
    });

    eventDeepLinkUrl = Linking.addEventListener('url', (e) => {
      return handleUrlDeepLink(e);
    });
    return () => {
      if (eventDeepLinkUrl) {
        eventDeepLinkUrl.remove();
      }
    };
  }, []);

  useEffect(() => {
    // action page
    const {
      attributes: { onVisit, componentActions },
    } = screen;

    if (onVisit && onVisit?.actionId) {
      const arrayAction = get(componentActions, `${onVisit?.actionId}.actions`);

      actionPromise({
        actionId: onVisit?.actionId,
        clientValues: dependencies,
        screenUuid: screen.screenUuid,
        screenAction: true,
        messages,
        arrayAction,
      });
    }
  }, [screen.screenUuid]);

  const isWeb = useMemo(() => appInfo?.platform === 'web', [appInfo]);
  const isPullToRefresh = useMemo(
    () => screen?.attributes?.pullToRefresh || false,
    [screen]
  );

  const isShowStatusBar =
    !screen.isModal &&
    !(Platform.OS === 'android' || Platform.OS === 'ios') &&
    !hiddenStatusBar &&
    screen?.attributes?.statusBarStyle !== 'hidden';

  // const originX = dimension.width / screen.width;
  const originY = dimension.height / screen.height;

  let paddingBottom = getPaddingBottomScreen(screen, originY);
  let arrNomal = getAttributesNormal(screen);
  let arrFixed = getAttributesFixed(screen);

  return loadingPage ? (
    <Loading />
  ) : (
    <View
      style={{
        backgroundColor: screen?.attributes?.backgroundColor,
        flex: 1,
        overflow: 'hidden',
      }}
    >
      {screen?.attributes?.backgroundImage && (
        <BackgroundImage
          source={screen?.attributes?.backgroundImage}
          resizeMode={screen?.attributes?.backgroundSize}
          positionX={screen?.attributes?.backgroundPositionX}
          positionY={screen?.attributes?.backgroundPositionY}
        />
      )}
      {!isWeb && (
        <StatusBar
          isShowStatusBar={isShowStatusBar}
          barStyle={screen?.attributes?.statusBarStyle}
        />
      )}
      <SafeAreaView
        style={{
          flex: 1,
          ...(Platform.OS === 'ios'
            ? { marginTop: StatusBarRN.currentHeight, marginBottom: 15 }
            : {}),
        }}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'android' ? undefined : 'padding'}
          style={{ flex: 1 }}
          // keyboardVerticalOffset={50}
        >
          <ScrollView
            bounces={isPullToRefresh}
            contentInsetAdjustmentBehavior="automatic"
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            ref={ref}
            // style={{ flex: 1, paddingBottom: paddingBottom }}
            contentContainerStyle={{
              flexGrow: 1,
              paddingBottom: paddingBottom,
            }}
          >
            {arrNomal.length > 0 && (
              <ObjectRender
                arrComp={arrNomal}
                isScreen={true}
                isShowStatusBar={isShowStatusBar}
                screen={screen}
              />
            )}
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
      <View
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1,
          height: '100%',
        }}
        pointerEvents="box-none"
      >
        <KeyboardAvoidingView
          behavior="height"
          style={{ flex: 1 }}
          pointerEvents="box-none"
          enabled={true}
          keyboardVerticalOffset={0}
        >
          {arrFixed.length > 0 && (
            <ObjectRender arrComp={arrFixed} isScreen={true} screen={screen} />
          )}
        </KeyboardAvoidingView>
      </View>
    </View>
  );
}

export default RenderConnector(Render);
